import { Avatar, Box, Typography, Tooltip } from '@mui/material';
import { getNameInitials } from '@utils/regex';
import { formatInTimeZone } from 'date-fns-tz';
import { MessagesSearchResponse } from '@lio/api-types';
import { HtmlContent } from '@components/noteMessage/CardNote';
import { QuoteTypeLabels } from '@models/quotes';
import { CardAttachmentFile } from '@components/noteMessage/CardAttachmentFile';
import { getMediaType } from '@utils/fileUtils';
import { LongTwelveHourDateTimeFormat } from '@utils/date';

export interface MessageBubbleProps {
  message: MessagesSearchResponse['items'][number];
  timezone?: string; // falls back to browser tz
  align?: 'left' | 'right';
  fullWidthMode?: boolean;
}

export const MessageBubble = ({ message, timezone, align = 'left', fullWidthMode }: MessageBubbleProps) => {
  const name = `${message.createdByUser?.firstName ?? ''} ${message.createdByUser?.lastName ?? ''}`.trim();
  const initials = getNameInitials(name || 'NA');
  const createdDate = new Date(message.createdAt);
  const timestamp = formatInTimeZone(
    createdDate,
    timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    LongTwelveHourDateTimeFormat,
  );

  const isLeft = align === 'left';

  const avatarSize = fullWidthMode ? 30 : 40;
  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: isLeft ? 'row' : 'row-reverse',
        gap: 1,
      }}
    >
      <Box
        sx={[
          {
            width: '100%',
            flexDirection: isLeft ? 'row' : 'row-reverse',
            display: 'flex',
            gap: 1,
          },
          fullWidthMode
            ? {
                px: 1,
              }
            : {},
        ]}
      >
        <Tooltip title={name || 'Unknown user'} placement='top' arrow disableInteractive>
          <Avatar
            sx={{
              bgcolor: isLeft ? 'primary.dark' : 'accent.secondary',
              fontSize: 12,
              width: avatarSize,
              height: avatarSize,
            }}
            aria-label={name || 'Unknown user'}
          >
            {initials}
          </Avatar>
        </Tooltip>
        <Box
          sx={[
            isLeft
              ? {
                  width: '100%',
                }
              : {
                  display: 'flex',
                  alignItems: 'flex-end',
                  flexDirection: 'column',
                },
            fullWidthMode
              ? {
                  overflow: 'hidden',
                  width: '100%',
                  boxShadow: 2,
                  borderRadius: 3,
                }
              : {},
          ]}
        >
          <Box
            sx={(theme) => ({
              backgroundColor: isLeft ? theme.palette.background.paper : theme.palette.primary.dark,
              color: isLeft ? theme.palette.text.primary : theme.palette.common.white,

              pr: isLeft ? 1.5 : 1,
              pl: isLeft ? 1 : 1.5,
              py: 1,
              borderRadius: 3,

              // for fullWidthMode the box shadow is handled on the outer box
              boxShadow: fullWidthMode ? 'none' : 2,

              wordBreak: 'break-word',
              display: 'flex',
              gap: fullWidthMode ? 1 : 3,
              flexDirection: 'column',
              width: fullWidthMode ? '100%' : 'fit-content',
              minWidth: fullWidthMode ? '100%' : { xs: '100%', sm: 0 },
              maxWidth: fullWidthMode ? '100%' : { xs: '100%', sm: '80%' },
            })}
          >
            {fullWidthMode && (
              <Typography variant='caption' sx={{ mt: 0.5, color: isLeft ? 'text.secondary' : '#c7c7c7' }}>
                {timestamp}
              </Typography>
            )}
            <HtmlContent
              body={message.body}
              sxProps={{
                color: 'inherit',
                lineHeight: '20px',
                '& p': { margin: 0 },
                '& p + p': { marginTop: '4px' },
              }}
            />
            {message?.artifacts && message.artifacts.length > 0 && (
              <Box sx={sxStyles.attachments.wrapper}>
                <Typography variant='caption' color='text.subdued'>
                  Attachments
                </Typography>
                {message.artifacts.map((artifact: any) => (
                  <Box key={artifact.id} sx={sxStyles.attachments.item}>
                    <CardAttachmentFile
                      {...artifact}
                      description={null}
                      variant='dense'
                      allowViewInModal
                      type={getMediaType(artifact.type)}
                      isNote={false}
                      state='sent'
                      menuItems={[]}
                    />
                  </Box>
                ))}
              </Box>
            )}
            {fullWidthMode && (
              <Typography
                variant='caption'
                sx={{
                  mt: 0.5,
                  color: isLeft ? 'text.secondary' : '#c7c7c7',
                  textAlign: 'right',
                }}
              >
                {message?.quote?.quoteType && QuoteTypeLabels.hasOwnProperty(message.quote.quoteType)
                  ? QuoteTypeLabels[message.quote.quoteType as keyof typeof QuoteTypeLabels]
                  : 'Policy'}
              </Typography>
            )}
          </Box>
          {!fullWidthMode && (
            <Typography variant='caption' sx={{ display: 'block', mt: '2px', color: 'text.secondary' }}>
              {timestamp} |{' '}
              {message?.quote?.quoteType && QuoteTypeLabels.hasOwnProperty(message.quote.quoteType)
                ? QuoteTypeLabels[message.quote.quoteType as keyof typeof QuoteTypeLabels]
                : 'Policy'}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const sxStyles = {
  attachments: {
    wrapper: {
      mb: 2,
      borderTop: '1px solid gray',
      borderColor: 'border.main',
      mt: 1,
      pt: 1,
      gap: 0.25,
      width: '100%',
    },
    item: {},
  },
};
